/**
 * User Context
 * Manages current userId, role, name, phone, and email in memory with write-through localStorage persistence
 * Also embeds CasbinContext for authorization management
 */

import { AuthLogic } from '@/modules/iam';
import { ClaimsLogic, type Claims } from '@/modules/idv';
import type { User } from './types';
import { UserLogic } from './logic';
import { trimOrUndefined } from '@/util/string';
import type { Phone } from '@/modules/phone';
import { PhoneLogic } from '@/modules/phone';
import { getCrossTabCommunication } from '@/services/crossTabCommunication';
import { StorageService } from '@/services/storage';
import { useIonRouter } from '@ionic/react';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { CasbinProvider } from '../../contexts/CasbinContext';

// Every string the context exposes (userId, the names, pronouns and email) is
// trimmed on the way in — by the setters, the restore from storage and the
// backend refresh — and a whitespace-only value is stored as undefined, so
// consumers can use them as-is.
export interface UserContextType {
  userId?: string;
  claims: Claims;
  // The name the user uses to identify themselves in a text message to a close
  // contact.
  familiarName?: string;
  // A short name or initials that friends-of-friends can use to refer to the user.
  allowedName?: string;
  // The user's preferred pronouns (e.g. "they/them"), used when others pass one
  // of their statements along.
  preferredPronouns?: string;
  phone?: Phone;
  email?: string;
  loading: boolean;
  hasUserId: boolean;
  setFamiliarName: (familiarName?: string) => void;
  setAllowedName: (allowedName?: string) => void;
  setPreferredPronouns: (preferredPronouns?: string) => void;
  // Commit a phone number: write-through to storage and clear any pending
  // change.
  setPhone: (phone?: Phone) => void;
  // Set a not-yet-verified number in memory (so the verification modal can use it)
  // WITHOUT committing it to storage, so reloading the page mid-verification
  // restores the previously stored number rather than this unverified edit. It is
  // stashed under PENDING_PHONE so a verified change can be promoted after an
  // out-of-band confirm (the SMS magic link). Commit it later with setPhone.
  setPendingPhone: (phone: Phone) => void;
  // Commit an email address: write-through to storage and clear any pending
  // change.
  setEmail: (email?: string) => void;
  // Set a not-yet-verified address in memory (so the verification modal can use
  // it) WITHOUT committing it to storage, so reloading the page mid-verification
  // restores the previously stored address rather than this unverified edit. It
  // is stashed under PENDING_EMAIL so a verified change can be promoted after
  // an out-of-band confirm (the email magic link). Commit it later with setEmail.
  setPendingEmail: (email: string) => void;
  // Abandon any not-yet-verified phone/email edit: drop the pending stash and
  // restore in-memory phone/email to the committed (last-verified) values in
  // storage. Returns those committed values. Used when the profile wizard reaches
  // its summary — by then an unverified change is given up on.
  discardPendingChanges: () => Promise<{ phone?: Phone; email?: string }>;
  refreshUserContext: () => Promise<User | null>;
}

export const UserContext = createContext<UserContextType | undefined>(
  undefined
);

interface UserProviderProps {
  children: React.ReactNode;
}

function logStorageError(error: unknown): void {
  // eslint-disable-next-line no-console
  console.error('Failed to persist user data to storage:', error);
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // Get current location from React Router (now available since UserProvider is inside Router)
  const location = useLocation();
  const router = useIonRouter();

  // In-memory userId, role, names, phone, and email state
  const [userId, setUserIdState] = useState<string | undefined>(undefined);
  const [claims, setClaimsState] = useState<Claims>(ClaimsLogic.create());
  const [familiarName, setFamiliarNameState] = useState<string | undefined>(
    undefined
  );
  const [allowedName, setAllowedNameState] = useState<string | undefined>(
    undefined
  );
  const [preferredPronouns, setPreferredPronounsState] = useState<
    string | undefined
  >(undefined);
  const [phone, setPhoneState] = useState<Phone | undefined>(undefined);
  const [email, setEmailState] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // Gate write-through persistence until the initial restore-from-storage and
  // backend refresh have completed, so the persist effects below don't clobber
  // stored values with the initial `undefined`s before they're loaded.
  const restoredRef = useRef(false);

  // A phone value (phone.value) set provisionally via setPhone(_, {persist:false})
  // that the write-through effect must NOT persist. Holds the unverified number
  // during a phone-change verification so a reload restores the prior number.
  const pendingPhoneRef = useRef<string | null>(null);

  // An email value set provisionally via setPendingEmail that the
  // write-through effect must NOT persist, and that refreshUserContext must NOT
  // overwrite with the still-committed (old) backend address. Holds the
  // unverified address during an email-change verification so a reload restores
  // the prior address.
  const pendingEmailRef = useRef<string | null>(null);

  // Track last processed pathname to avoid processing on back/forward navigation
  // Only process verified=true when pathname changes (new navigation), not on search param changes alone
  const lastProcessedPathnameRef = useRef<string | null>(null);

  // Generation counter to discard stale refreshUserContext responses when
  // multiple calls race (e.g., mount effect vs IDV page confirm).
  const refreshGenerationRef = useRef(0);

  const refreshUserContext = useCallback(async (): Promise<User | null> => {
    const generation = ++refreshGenerationRef.current;
    const authService = AuthLogic.getAuthService();

    try {
      const fetched = await authService.getCurrentUser();
      const user = fetched && UserLogic.withTrimmedText(fetched);

      if (generation !== refreshGenerationRef.current) return null;

      if (user) {
        setClaimsState(user.claims);
        setUserIdState(user.id);

        // Don't overwrite an in-flight pending (unverified) email with the
        // still-committed backend address — the modal refreshes periodically and
        // would otherwise clobber the address being verified. Once the change is
        // committed (setEmail clears the ref), normal refresh resumes.
        if (user.email !== undefined && pendingEmailRef.current === null) {
          setEmailState(user.email);
        }
      } else {
        setUserIdState(undefined);
        setClaimsState(ClaimsLogic.create());
      }
      return user;
    } catch (error) {
      if (generation !== refreshGenerationRef.current) return null;
      // eslint-disable-next-line no-console
      console.error('Failed to refresh user data from server:', error);
      setClaimsState(ClaimsLogic.create());
      return null;
    }
  }, []);

  // Initialize on mount: restore local storage data and establish a JWT session in parallel.
  // loading stays true until both complete so that auth-dependent components (e.g. CasbinContext,
  // SnowballSync) don't fire API requests before the guest cookie is set.
  useEffect(() => {
    const restoreNonJwtUserDataFromStorage = async () => {
      try {
        const [
          loadedFamiliarName,
          loadedAllowedName,
          loadedPreferredPronouns,
          loadedPhoneStr,
          loadedEmail,
        ] = await Promise.all([
          StorageService.getFamiliarName(),
          StorageService.getAllowedName(),
          StorageService.getPreferredPronouns(),
          StorageService.getPhone(),
          StorageService.getEmail(),
        ]);
        setFamiliarNameState(trimOrUndefined(loadedFamiliarName));
        setAllowedNameState(trimOrUndefined(loadedAllowedName));
        setPreferredPronounsState(trimOrUndefined(loadedPreferredPronouns));
        setEmailState(trimOrUndefined(loadedEmail));

        // Convert phone string to Phone if valid
        if (loadedPhoneStr) {
          const phoneObj = PhoneLogic.tryCreate(loadedPhoneStr) ?? undefined;
          setPhoneState(phoneObj);
        } else {
          setPhoneState(undefined);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load user data from storage:', error);
        setFamiliarNameState(undefined);
        setAllowedNameState(undefined);
        setPreferredPronounsState(undefined);
        setPhoneState(undefined);
        setEmailState(undefined);
      }
    };

    const init = async () => {
      try {
        const [, user] = await Promise.all([
          restoreNonJwtUserDataFromStorage(),
          refreshUserContext(),
        ]);
        // Persist email fetched from the backend. Write-through is gated until
        // restoredRef flips below, so this initial backend value is written
        // explicitly here (preserves the previous needsFlushEmail behavior).
        if (user?.email) {
          await StorageService.setEmail(user.email).catch(logStorageError);
        }
      } finally {
        restoredRef.current = true;
        setLoading(false);
      }
    };

    init().catch(error => {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize user session:', error);
      setLoading(false);
    });
  }, [refreshUserContext]);

  // Persist familiar name to storage whenever it changes (write-through).
  // Keystroke coalescing for native performance is tracked in #415.
  useEffect(() => {
    if (!restoredRef.current) return;
    const op = familiarName
      ? StorageService.setFamiliarName(familiarName)
      : StorageService.clearFamiliarName();
    op.catch(logStorageError);
  }, [familiarName]);

  // Persist allowed name to storage whenever it changes (write-through).
  useEffect(() => {
    if (!restoredRef.current) return;
    const op = allowedName
      ? StorageService.setAllowedName(allowedName)
      : StorageService.clearAllowedName();
    op.catch(logStorageError);
  }, [allowedName]);

  // Persist preferred pronouns to storage whenever they change (write-through).
  useEffect(() => {
    if (!restoredRef.current) return;
    const op = preferredPronouns
      ? StorageService.setPreferredPronouns(preferredPronouns)
      : StorageService.clearPreferredPronouns();
    op.catch(logStorageError);
  }, [preferredPronouns]);

  // Persist phone to storage whenever it changes (write-through).
  // Keystroke coalescing for native performance is tracked in #415.
  useEffect(() => {
    if (!restoredRef.current) return;
    // Skip a pending, not-yet-verified number so it never reaches storage.
    if (pendingPhoneRef.current === (phone?.value ?? '')) return;
    const op = phone
      ? StorageService.setPhone(phone.value)
      : StorageService.clearPhone();
    op.catch(logStorageError);
  }, [phone]);

  // Persist email to storage whenever it changes (write-through).
  // Keystroke coalescing for native performance is tracked in #415.
  useEffect(() => {
    if (!restoredRef.current) return;
    // Skip a pending, not-yet-verified address so it never reaches the
    // committed EMAIL key.
    if (pendingEmailRef.current === (email ?? '')) return;
    const op = email
      ? StorageService.setEmail(email)
      : StorageService.clearEmail();
    op.catch(logStorageError);
  }, [email]);

  const setFamiliarName = useCallback((newName: string | undefined) => {
    setFamiliarNameState(trimOrUndefined(newName));
  }, []);

  const setAllowedName = useCallback((newName: string | undefined) => {
    setAllowedNameState(trimOrUndefined(newName));
  }, []);

  const setPreferredPronouns = useCallback(
    (newPronouns: string | undefined) => {
      setPreferredPronounsState(trimOrUndefined(newPronouns));
    },
    []
  );

  const setPhone = useCallback((newPhone: Phone | undefined) => {
    // A committing set supersedes any pending change. The write-through
    // effect persists the new value to the committed PHONE key (callers always
    // pass a fresh Phone, so the state change fires the effect).
    pendingPhoneRef.current = null;
    StorageService.clearPendingPhone().catch(logStorageError);
    setPhoneState(newPhone);
  }, []);

  const setPendingPhone = useCallback((newPhone: Phone) => {
    // Keep the pending (unverified) number out of the committed PHONE key —
    // the write-through effect skips it (see pendingPhoneRef) — so a reload
    // mid-verification restores the prior number. Stash it under PENDING_PHONE
    // so a verification that completes out of band (SMS magic link) can still be
    // promoted to the committed number.
    pendingPhoneRef.current = newPhone.value;
    StorageService.setPendingPhone(newPhone.value).catch(logStorageError);
    setPhoneState(newPhone);
  }, []);

  const setEmail = useCallback((untrimmedEmail: string | undefined) => {
    const newEmail = trimOrUndefined(untrimmedEmail);
    // A committing set supersedes any pending change. Persist the committed
    // value directly: it often equals the pending value already in state
    // (promoting the just-verified address), in which case setEmailState would
    // bail and the write-through effect wouldn't fire — so write it here to
    // guarantee the committed EMAIL key is updated. (Phone avoids this because
    // each setPhone passes a fresh Phone object; email values are plain strings.)
    pendingEmailRef.current = null;
    StorageService.clearPendingEmail().catch(logStorageError);
    const op = newEmail
      ? StorageService.setEmail(newEmail)
      : StorageService.clearEmail();
    op.catch(logStorageError);
    setEmailState(newEmail);
  }, []);

  const setPendingEmail = useCallback((untrimmedEmail: string) => {
    const newEmail = untrimmedEmail.trim();
    // Set the in-memory context email so the verification modal (and the step's
    // UI) can send/show the address while it's being verified — but keep it OUT
    // of the committed EMAIL key: the write-through effect skips a pending
    // value (see pendingEmailRef), so a reload mid-verification restores the
    // prior address rather than this unverified edit. Stash it under
    // PENDING_EMAIL so a verification that completes out of band (email magic
    // link) can still be promoted to the committed address. Mirrors
    // setPendingPhone — "pending" means in memory only, not yet committed.
    pendingEmailRef.current = newEmail;
    StorageService.setPendingEmail(newEmail).catch(logStorageError);
    setEmailState(newEmail);
  }, []);

  const discardPendingChanges = useCallback(async (): Promise<{
    phone?: Phone;
    email?: string;
  }> => {
    // Drop any not-yet-verified phone/email edit and restore in-memory state to
    // the committed (last-verified) values in storage. The write-through effects
    // skipped the pending values, so the committed keys still hold the prior
    // verified ones. Clearing the pending refs first lets the effects re-persist
    // the restored values normally.
    pendingPhoneRef.current = null;
    pendingEmailRef.current = null;
    await Promise.all([
      StorageService.clearPendingPhone().catch(logStorageError),
      StorageService.clearPendingEmail().catch(logStorageError),
    ]);
    const [committedPhoneStr, storedEmail] = await Promise.all([
      StorageService.getPhone(),
      StorageService.getEmail(),
    ]);
    const committedPhone = committedPhoneStr
      ? (PhoneLogic.tryCreate(committedPhoneStr) ?? undefined)
      : undefined;
    const committedEmail = trimOrUndefined(storedEmail);
    setPhoneState(committedPhone);
    setEmailState(committedEmail);
    return { phone: committedPhone, email: committedEmail };
  }, []);

  const hasUserId = userId !== undefined;

  // Check for verified=true query parameter after IDV redirect
  // Only process when pathname changes (new navigation), not on back/forward navigation
  useEffect(() => {
    const currentPathname = location.pathname;
    const lastPathname = lastProcessedPathnameRef.current;

    // Only process if this is a new navigation (pathname changed)
    // This avoids processing on back/forward navigation that only changes search params
    if (currentPathname !== lastPathname) {
      lastProcessedPathnameRef.current = currentPathname;

      // Check for verified=true in search params using React Router's location
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('verified') === 'true') {
        // IDV was just completed - refresh user context to get new trust level
        refreshUserContext().catch(error => {
          // eslint-disable-next-line no-console
          console.error('Failed to refresh user context after IDV:', error);
        });
      }
    }
  }, [location.pathname, location.search, refreshUserContext]);

  // Listen for IDV completion from other tabs (cross-tab communication)
  // This handles the case where user verifies in Tab B and Tab A should update and redirect
  useEffect(() => {
    const crossTab = getCrossTabCommunication();
    const unsubscribe = crossTab.onIDVCompletion(async message => {
      try {
        await refreshUserContext();
        if (message.continuePath) {
          // Strip query params from continuePath (they're only used for spawned tabs)
          const pathWithoutQuery = message.continuePath.split('?')[0];
          router.push(pathWithoutQuery, 'forward', 'replace');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh user context after IDV:', error);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [router, refreshUserContext]);

  const value = useMemo(
    () => ({
      userId,
      claims,
      familiarName: familiarName ?? undefined,
      allowedName: allowedName ?? undefined,
      preferredPronouns: preferredPronouns ?? undefined,
      phone: phone ?? undefined,
      email: email ?? undefined,
      loading,
      hasUserId,
      setFamiliarName,
      setAllowedName,
      setPreferredPronouns,
      setPhone,
      setPendingPhone,
      setEmail,
      setPendingEmail,
      discardPendingChanges,
      refreshUserContext,
    }),
    [
      userId,
      claims,
      familiarName,
      allowedName,
      preferredPronouns,
      phone,
      email,
      loading,
      hasUserId,
      setFamiliarName,
      setAllowedName,
      setPreferredPronouns,
      setPhone,
      setPendingPhone,
      setEmail,
      setPendingEmail,
      discardPendingChanges,
      refreshUserContext,
    ]
  );

  return (
    <UserContext.Provider value={value}>
      <CasbinProvider>{children}</CasbinProvider>
    </UserContext.Provider>
  );
};
